/**
 * httpError (UX-11) — one transport-error classifier for the whole client.
 *
 * THE DEFECT UX-11 CLOSES: a server-side VALIDATION failure (HTTP 422, or a
 * 400 carrying a validation code) is a comment about the REQUEST, not a
 * finding about the tenant's compliance. Before this module the scanner and
 * the compliance console rendered every non-2xx as a red error beside real
 * statutory violations, so a malformed payload looked indistinguishable from
 * "this landlord is out of compliance". A reviewer cannot tell a broken
 * request from a broken tenant — that is the safety line this draws.
 *
 * CONTRACT, stated so it can't be quietly weakened later:
 *   - `classifyHttpError` is total: it never throws and always returns a
 *     classified shape, including for bare `Error('Network Error')`.
 *   - `isValidation` is true ONLY for request-shape failures: status 422,
 *     status 400, or a 4xx carrying a recognised validation code. Nothing else.
 *   - `isComplianceViolation` is ALWAYS false. A transport error is a fact
 *     about the wire, never about the landlord — this is enforced by
 *     construction, not by convention. Compliance findings come from the
 *     violation payload only.
 *
 * No imports: this module has zero dependencies so both the axios
 * interceptors (services/api.ts) and any leaf component can use it without a
 * cycle.
 */

export type HttpErrorKind =
  | 'validation'
  | 'rate_limit'
  | 'server'
  | 'auth'
  | 'forbidden'
  | 'not_found'
  | 'network'
  | 'unknown';

export interface HttpValidationIssue {
  field?: string;
  message: string;
}

export interface ClassifiedHttpError {
  kind: HttpErrorKind;
  /** HTTP status when one exists; undefined for transport failures. */
  status?: number;
  /** Server error code (`VALIDATION_ERROR`, `AUTH_REQUIRED`, …) when present. */
  code?: string;
  /** Human-readable message, always non-empty. */
  message: string;
  /** Field-level issues from the server (empty when none were provided). */
  issues: HttpValidationIssue[];
  /** True ONLY for request-shape failures (400/422, or a validation code). */
  isValidation: boolean;
  /** Transport/HTTP failures are NEVER compliance violations. Always false. */
  isComplianceViolation: false;
}

/** Server codes that mark a 400/422 as a request-shape (validation) failure. */
const VALIDATION_CODES = new Set([
  'VALIDATION_ERROR',
  'VALIDATION_FAILED',
  'X_VALIDATION',
  'INVALID_REQUEST',
  'INVALID_PAYLOAD',
  'UNPROCESSABLE_ENTITY',
  'MISSING_FIELD',
  'BAD_REQUEST',
]);

const KIND_BY_STATUS: Record<number, HttpErrorKind> = {
  400: 'validation',
  401: 'auth',
  403: 'forbidden',
  404: 'not_found',
  422: 'validation',
  429: 'rate_limit',
};

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v : undefined;

const codeOf = (data: any): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  return (
    asString(data.code) ||
    asString(data.errorCode) ||
    asString(data.type) ||
    // `error` is frequently a machine code and sometimes a sentence; keep it
    // as a code only when it looks like an identifier (SCREAMING_SNAKE).
    (typeof data.error === 'string' && /^[A-Z][A-Z0-9_]*$/.test(data.error)
      ? data.error
      : undefined)
  );
};

const collectIssues = (data: any): HttpValidationIssue[] => {
  if (!data || typeof data !== 'object') return [];
  const raw = Array.isArray(data.issues)
    ? data.issues
    : Array.isArray(data.errors)
      ? data.errors
      : Array.isArray(data.details)
        ? data.details
        : null;
  if (!raw) return [];
  return raw
    .map((entry: any): HttpValidationIssue | null => {
      if (typeof entry === 'string') return { message: entry };
      if (!entry || typeof entry !== 'object') return null;
      const field = asString(entry.field) || asString(entry.path) || asString(entry.param);
      const message =
        asString(entry.message) ||
        asString(entry.msg) ||
        asString(entry.error) ||
        asString(entry.detail);
      if (!message && !field) return null;
      return { field, message: message || `Invalid value for ${field}` };
    })
    .filter((i: HttpValidationIssue | null): i is HttpValidationIssue => i !== null);
};

/**
 * Classify any thrown value. Never throws. Idempotent: an error already
 * carrying a classification (attached by the axios interceptor) is returned
 * as-is.
 */
export function classifyHttpError(err: any): ClassifiedHttpError {
  if (err && typeof err === 'object' && err.__httpClass) {
    return err.__httpClass as ClassifiedHttpError;
  }

  const response = err?.response;
  const status: number | undefined =
    typeof response?.status === 'number'
      ? response.status
      : typeof err?.status === 'number'
        ? err.status
        : undefined;
  const data = response?.data ?? err?.data;
  const code = codeOf(data) || asString(err?.code);
  const issues = collectIssues(data);

  // No HTTP response at all → transport failure (offline, DNS, CORS, abort).
  if (status === undefined) {
    return {
      kind: 'network',
      status: undefined,
      code,
      message:
        asString(data?.message) ||
        asString(err?.message) ||
        'Network error — please check your connection and try again.',
      issues,
      isValidation: false,
      isComplianceViolation: false,
    };
  }

  let kind: HttpErrorKind =
    KIND_BY_STATUS[status] || (status >= 500 ? 'server' : 'unknown');

  // A recognised validation code promotes any otherwise-generic 4xx to
  // `validation`. (400 and 422 are already validation via the status map.)
  if (kind === 'unknown' && code && VALIDATION_CODES.has(code)) {
    kind = 'validation';
  }

  // THE hard line: only request-shape failures are validation. Anything else
  // (network, server, auth, …) is never a compliance violation.
  const isValidation = kind === 'validation';

  return {
    kind,
    status,
    code,
    // Prefer the server's own words; fall back to a kind-specific default
    // BEFORE the generic axios string ("Request failed with status code
    // 429"), which tells a user nothing.
    message:
      asString(issues[0]?.message) ||
      asString(data?.message) ||
      asString(data?.error) ||
      defaultMessageFor(kind) ||
      asString(err?.message) ||
      'Something went wrong processing this request.',
    issues,
    isValidation,
    isComplianceViolation: false,
  };
}

function defaultMessageFor(kind: HttpErrorKind): string {
  switch (kind) {
    case 'validation':
      return 'The request could not be validated. Please review the details and try again.';
    case 'rate_limit':
      return 'Too many requests — please wait a moment and try again.';
    case 'server':
      return 'The server hit an error processing this request. Please try again shortly.';
    case 'auth':
      return 'Your session has expired — please sign in again.';
    case 'forbidden':
      return 'You do not have permission to perform this action.';
    case 'not_found':
      return 'The requested resource was not found.';
    case 'network':
      return 'Network error — please check your connection and try again.';
    default:
      return 'Something went wrong processing this request.';
  }
}

/** Convenience: is this a request-shape (validation) failure? */
export function isValidationError(err: any): boolean {
  return classifyHttpError(err).isValidation;
}

/**
 * UX-11's hard line: a transport error is NEVER a compliance violation.
 * Exists as a named function so call sites read as intent, and so the
 * invariant is asserted directly in tests.
 */
export function isComplianceViolation(_err: any): boolean {
  return false;
}

/** A user-facing message for any error, preferring field-level detail on 422. */
export function errorToMessage(err: any): string {
  return classifyHttpError(err).message;
}
